import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, LogOut, DollarSign, Package, CheckCircle2, CalendarClock, Lock, TrendingUp,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  adminLogin, adminLogout, adminMe, fetchAdminBookings, fetchAdminStats,
  updateBookingStatus, setAdminToken,
} from '../lib/api';

const STATUS_LABELS = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  picked_up: 'Recogida',
  in_progress: 'En proceso',
  delivered: 'Entregada',
  cancelled: 'Cancelada',
};

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-cyan-100 text-cyan-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-600',
};

const PAYMENT_LABELS = {
  paid: 'Pagado',
  unpaid: 'Sin pagar',
  failed: 'Fallido',
  refunded: 'Reembolsado',
};

const PAYMENT_COLORS = {
  paid: 'bg-emerald-100 text-emerald-700',
  unpaid: 'bg-gray-100 text-gray-500',
  failed: 'bg-red-100 text-red-600',
  refunded: 'bg-purple-100 text-purple-700',
};

const PLAN_LABELS = {
  essential: 'Essential · $39.99 pago único',
  plus: 'Plus · $99.99/mes',
  premium: 'Premium · $189.99/mes',
};

const DETERGENT_LABELS = {
  standard: 'Estándar',
  hypoallergenic: 'Hipoalergénico',
};

const EXTRAS_LABELS = {
  comforter: { name: 'Edredón', price: 25 },
  small_rug: { name: 'Alfombra pequeña', price: 15 },
  king_sheets: { name: 'Sábanas king', price: 15 },
  queen_sheets: { name: 'Sábanas queen', price: 10 },
  combo_bed_king: { name: 'Combo Cama King', price: 35 },
  combo_bed_queen: { name: 'Combo Cama Queen', price: 30 },
  express_pickup: { name: 'Delivery Express 24h', price: 30, highlight: true },
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Algo salió mal. Inténtalo de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .join(' ');
  }
  return String(detail);
};

const AdminLogin = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await adminLogin(email, password);
      setAdminToken(data.access_token);
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    'w-full bg-[#F5F5F7] border-2 border-transparent focus:bg-white focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 rounded-2xl px-5 py-4 text-[15px] text-[#1D1D1F] placeholder:text-[#86868B] transition-all outline-none';

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-[2rem] p-8 sm:p-10 shadow-[0_18px_60px_rgba(0,0,0,0.08)] border border-black/5"
      >
        <div className="mx-auto w-12 h-12 rounded-full bg-[#0071E3]/10 flex items-center justify-center mb-5">
          <Lock className="w-5 h-5 text-[#0071E3]" />
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight text-[#1D1D1F] text-center">
          Panel de administración
        </h1>
        <p className="mt-2 text-sm text-[#86868B] text-center">
          Acceso exclusivo para el administrador.
        </p>
        <div className="mt-8 space-y-4">
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className={inputBase}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            className={inputBase}
          />
        </div>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600 text-center">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="mt-6 w-full bg-[#0071E3] hover:bg-[#0077ED] disabled:opacity-60 text-white font-semibold rounded-full px-8 py-3.5 transition-transform hover:scale-[1.02] active:scale-95 inline-flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Entrar'}
        </button>
      </form>
    </div>
  );
};

const StatCard = ({ testId, icon: Icon, label, value, sub }) => (
  <div
    data-testid={testId}
    className="bg-[#F5F5F7] rounded-3xl p-6 flex flex-col gap-1"
  >
    <div className="flex items-center gap-2 text-[#86868B]">
      <Icon className="w-4 h-4" />
      <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
    </div>
    <span className="text-3xl font-extrabold tracking-tight text-[#1D1D1F]">{value}</span>
    {sub && <span className="text-xs text-[#86868B]">{sub}</span>}
  </div>
);

const AdminDashboard = ({ admin, onLogout }) => {
  const [bookings, setBookings] = useState(null);
  const [stats, setStats] = useState(null);
  const [updating, setUpdating] = useState(null);

  const load = async () => {
    try {
      const [b, s] = await Promise.all([fetchAdminBookings(), fetchAdminStats()]);
      setBookings(b);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
      else toast.error('No se pudieron cargar los datos');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeStatus = async (id, status) => {
    setUpdating(id);
    try {
      await updateBookingStatus(id, status);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Estado actualizado: ${STATUS_LABELS[status]}`);
    } catch {
      toast.error('No se pudo actualizar el estado');
    } finally {
      setUpdating(null);
    }
  };

  const planSummary = useMemo(() => {
    if (!stats) return '';
    const p = stats.by_plan || {};
    return `Essential ${p.essential || 0} · Plus ${p.plus || 0} · Premium ${p.premium || 0}`;
  }, [stats]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-white">
      <header className="px-6 sm:px-12 py-5 border-b border-black/5 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2.5">
          <img src="/logo-icon.png" alt="Premium Laundry Services" className="w-8 h-8 object-contain" />
          <span className="text-[15px] font-semibold tracking-tight text-[#1D1D1F]">
            Premium Laundry — Admin
          </span>
        </Link>
        <div className="flex items-center gap-4">
          <span className="hidden sm:block text-sm text-[#86868B]">{admin.email}</span>
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1D1D1F] bg-[#F5F5F7] hover:bg-[#EBEBED] rounded-full px-5 py-2.5 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1D1D1F]">
          Reservas
        </h1>

        {!stats || !bookings ? (
          <div className="mt-16 flex justify-center">
            <Loader2 className="w-8 h-8 text-[#0071E3] animate-spin" />
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                testId="admin-stat-revenue"
                icon={DollarSign}
                label="Ingresos del mes"
                value={`$${stats.revenue_month.toFixed(2)}`}
              />
              <StatCard
                testId="admin-stat-total"
                icon={Package}
                label="Reservas totales"
                value={stats.total_bookings}
                sub={planSummary}
              />
              <StatCard
                testId="admin-stat-paid"
                icon={CheckCircle2}
                label="Pagadas"
                value={stats.paid_bookings}
              />
              <StatCard
                testId="admin-stat-upcoming"
                icon={CalendarClock}
                label="Pickups próximos (7 días)"
                value={stats.upcoming_pickups.length}
              />
            </div>

            {stats.upsell_stats && stats.upsell_stats.email_upsell_clicks > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard
                  testId="admin-stat-upsell-clicks"
                  icon={TrendingUp}
                  label="Clicks Email Upsell"
                  value={stats.upsell_stats.email_upsell_clicks}
                  sub="Reservas que llegaron desde el email"
                />
                <StatCard
                  testId="admin-stat-upsell-converted"
                  icon={CheckCircle2}
                  label="Convirtieron a Plus/Premium"
                  value={stats.upsell_stats.email_upsell_converted_bookings}
                  sub={`${stats.upsell_stats.email_upsell_conversion_rate}% conversion rate`}
                />
                <StatCard
                  testId="admin-stat-upsell-paid"
                  icon={DollarSign}
                  label="Pagadas (upsell)"
                  value={stats.upsell_stats.email_upsell_paid_conversions}
                />
              </div>
            )}

            <div className="mt-10 overflow-x-auto rounded-3xl border border-black/5 shadow-[0_10px_40px_rgba(0,0,0,0.04)]">
              <table data-testid="admin-bookings-table" className="w-full text-left text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-[#F5F5F7] text-[#86868B] text-xs uppercase tracking-wider">
                    <th className="px-5 py-4 font-semibold">Cliente</th>
                    <th className="px-5 py-4 font-semibold">Plan</th>
                    <th className="px-5 py-4 font-semibold">Detergente</th>
                    <th className="px-5 py-4 font-semibold">Extras</th>
                    <th className="px-5 py-4 font-semibold">Pickup</th>
                    <th className="px-5 py-4 font-semibold">Direcciones</th>
                    <th className="px-5 py-4 font-semibold">Pago</th>
                    <th className="px-5 py-4 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-5 py-10 text-center text-[#86868B]">
                        No hay reservas todavía.
                      </td>
                    </tr>
                  )}
                  {bookings.map((b) => {
                    const hasExpress = (b.extras || []).includes('express_pickup');
                    return (
                    <tr
                      key={b.id}
                      data-testid={`admin-booking-row-${b.id}`}
                      className={`border-t border-black/5 align-top ${hasExpress ? 'bg-red-50/40' : ''}`}
                    >
                      <td className="px-5 py-4">
                        <p className="font-semibold text-[#1D1D1F]">{b.full_name}</p>
                        <p className="text-xs text-[#86868B]">{b.email}</p>
                        <p className="text-xs text-[#86868B]">{b.phone}</p>
                      </td>
                      <td className="px-5 py-4 text-[#1D1D1F]">{PLAN_LABELS[b.membership] || b.membership}</td>
                      <td className="px-5 py-4 text-[#1D1D1F]">{DETERGENT_LABELS[b.detergent] || 'Estándar'}</td>
                      <td className="px-5 py-4 max-w-[180px]">
                        {(b.extras && b.extras.length > 0) ? (
                          <div className="flex flex-wrap gap-1">
                            {b.extras.map((eid) => {
                              const e = EXTRAS_LABELS[eid] || { name: eid, price: 0 };
                              const cls = e.highlight
                                ? 'bg-red-100 text-red-700'
                                : 'bg-[#0071E3]/10 text-[#0071E3]';
                              return (
                                <span
                                  key={eid}
                                  className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}
                                  title={`+$${e.price.toFixed(2)}`}
                                >
                                  {e.highlight && <span aria-hidden="true">⚡</span>}
                                  {e.name}
                                </span>
                              );
                            })}
                            {b.extras_total > 0 && (
                              <span className="text-[11px] text-[#0071E3] font-bold w-full mt-0.5">
                                +${Number(b.extras_total || 0).toFixed(2)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-[#86868B]">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap text-[#1D1D1F]">
                        {b.pickup_date}
                        <span className="text-[#86868B]"> · {b.pickup_time}</span>
                      </td>
                      <td className="px-5 py-4 max-w-[220px]">
                        <p className="text-xs text-[#1D1D1F] truncate" title={b.pickup_address}>↑ {b.pickup_address}</p>
                        <p className="text-xs text-[#86868B] truncate" title={b.delivery_address}>↓ {b.delivery_address}</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full ${PAYMENT_COLORS[b.payment_status] || ''}`}>
                          {PAYMENT_LABELS[b.payment_status] || b.payment_status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <Select
                          value={b.status}
                          onValueChange={(v) => changeStatus(b.id, v)}
                          disabled={updating === b.id}
                        >
                          <SelectTrigger
                            data-testid={`admin-status-select-${b.id}`}
                            className={`h-8 w-36 rounded-full border-0 text-xs font-semibold ${STATUS_COLORS[b.status] || ''}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-2xl">
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value} className="rounded-xl text-sm">
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default function Admin() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Try to hydrate the admin from an existing session. This works both when
    // an HTTPOnly cookie is present (new flow) and when the legacy bearer
    // token is in localStorage (fallback).
    adminMe()
      .then(setAdmin)
      .catch(() => setAdminToken(null))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    // Fire-and-forget: clears the HTTPOnly cookie server-side.
    adminLogout();
    setAdminToken(null);
    setAdmin(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#0071E3] animate-spin" />
      </div>
    );
  }

  if (!admin) return <AdminLogin onSuccess={setAdmin} />;
  return <AdminDashboard admin={admin} onLogout={logout} />;
}
